import React, { useState } from "react";
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Typography,
  Box,
  LinearProgress,
  InputAdornment,
  IconButton,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { createGoal } from "../../api/goals";
import PlanIntelligenceSuggestion from "./PlanIntelligenceSuggestion";
import PendingActionNotice from "../common/PendingActionNotice";

function getFieldError(errors, field) {
  if (!errors || typeof errors !== "object") return "";
  const value = errors[field];
  if (Array.isArray(value) && value.length) return value[0];
  if (typeof value === "string") return value;
  return "";
}

function parseOutcome(value) {
  const trimmed = value.trim();
  if (!trimmed) {
    return { error: "Goal outcome is required." };
  }

  const match = trimmed.match(/([0-9][0-9,]*(?:\.[0-9]+)?)/);
  if (!match) {
    return { value: { targetValue: 1, unit: "goal" } };
  }

  const rawNumber = match[1].replace(/,/g, "");
  const parsed = Number(rawNumber);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return { value: { targetValue: 1, unit: "goal" } };
  }

  const remainder = trimmed.replace(match[1], "").trim();
  const unit = remainder || "goal";
  return { value: { targetValue: parsed, unit } };
}

function parseOptionalNumber(value) {
  const cleaned = value.trim().replace(/,/g, "");
  if (!cleaned) {
    return { value: null };
  }
  const parsed = Number(cleaned);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return { error: "Starting point must be a number." };
  }
  return { value: parsed };
}

function getDaysLeft(targetDate) {
  if (!targetDate) return null;
  const today = new Date();
  const selected = targetDate.toDate ? targetDate.toDate() : new Date(targetDate);
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const end = new Date(selected.getFullYear(), selected.getMonth(), selected.getDate());
  const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  return diff;
}

function formatDateForStorage(date) {
  if (!date) return "";
  const nativeDate = date.toDate ? date.toDate() : date;
  const year = nativeDate.getFullYear();
  const month = String(nativeDate.getMonth() + 1).padStart(2, "0");
  const day = String(nativeDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function GoalCreateDialog({ visionId, onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [targetDate, setTargetDate] = useState(null);
  const [startingValue, setStartingValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [tipsOpen, setTipsOpen] = useState(false);
  const [tipsDismissed, setTipsDismissed] = useState(false);

  async function handleSave() {
    setLoading(true);
    setFieldErrors({});
    setErrorMessage("");

    const nextErrors = {};

    if (!title.trim()) {
      nextErrors.title = "Goal title is required.";
    }

    if (!targetDate) {
      nextErrors.target_date = "Pick a target date.";
    }

    if (Object.keys(nextErrors).length) {
      setFieldErrors(nextErrors);
      setLoading(false);
      return;
    }

    const parsedOutcome = parseOutcome(title);
    if (parsedOutcome.error) {
      setFieldErrors({ title: parsedOutcome.error });
      setLoading(false);
      return;
    }

    const parsedStarting = parseOptionalNumber(startingValue);
    if (parsedStarting.error) {
      setFieldErrors({ starting_value: parsedStarting.error });
      setLoading(false);
      return;
    }

    try {
      const targetDateString = formatDateForStorage(targetDate);
      const createdGoal = await createGoal({
        vision: visionId,
        title: title.trim(),
        description: "",
        why_this_matters: "",
        target_date: targetDateString || null,
        unit: parsedOutcome.value.unit,
        target_value: parsedOutcome.value.targetValue,
        starting_value: parsedStarting.value,
        timeframe: "once",
      });
      onSaved?.(createdGoal);
      onClose();
    } catch (err) {
      console.error("Failed to create goal", err);
      setErrorMessage(err.message || "Failed to create goal.");
      if (err?.errors) {
        const serverErrors = {
          title: getFieldError(err.errors, "title"),
          description: getFieldError(err.errors, "description"),
          unit: getFieldError(err.errors, "unit"),
          target_value: getFieldError(err.errors, "target_value"),
          starting_value: getFieldError(err.errors, "starting_value"),
          timeframe: getFieldError(err.errors, "timeframe"),
        };
        const cleanedErrors = Object.fromEntries(
          Object.entries(serverErrors).filter(([, value]) => value)
        );
        if (Object.keys(cleanedErrors).length) {
          setFieldErrors(cleanedErrors);
        }
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create a Goal</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {loading ? (
            <PendingActionNotice
              compact
              title="Creating goal"
              body="Saving this goal and preparing the next plan step."
            />
          ) : null}

          <Typography variant="body2" color="text.secondary">
            A goal is the measurable outcome for this vision. After this, you will
            add one small habit you can repeat.
          </Typography>
          <TextField
            label="What do you want to achieve?"
            value={title}
            onChange={e => setTitle(e.target.value)}
            autoFocus
            fullWidth
            required
            error={Boolean(fieldErrors.title)}
            helperText={
              fieldErrors.title ||
              "Examples: Reach 238 pounds, Run 13 miles, Save 5,000"
            }
          />

          {(() => {
            if (tipsDismissed) return null;
            const outcome = parseOutcome(title || "");
            const targetValue = outcome?.value?.targetValue;
            const finite = /\b(finish|complete|read\b|read the\b|read entire|finish reading)\b/i.test(title || "");
            const shouldShow = targetValue === 1 || finite;
            if (!shouldShow) return null;
            return (
              <Box sx={{ border: "1px dashed", borderColor: "divider", borderRadius: 2, p: 1.5 }}>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setTipsOpen((prev) => !prev)}
                  sx={{ textTransform: "none", px: 0, minWidth: 0 }}
                >
                  {tipsOpen ? "Hide tips" : "Tips for tracking this goal"}
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                  Tip: Goals feel easier to stick with when progress can be seen day to day.
                </Typography>
                {tipsOpen && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                    For big goals, most people track something measurable like pages, minutes, or steps instead of the
                    whole outcome. This helps progress feel real and motivating.
                  </Typography>
                )}
                {tipsOpen && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setTipsDismissed(true)}
                    sx={{ textTransform: "none", px: 0, minWidth: 0, mt: 0.5 }}
                  >
                    Dismiss
                  </Button>
                )}
              </Box>
            );
          })()}

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="By when?"
              value={targetDate}
              onChange={(value) => setTargetDate(value)}
              disablePast
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: Boolean(fieldErrors.target_date),
                  helperText: fieldErrors.target_date,
                },
              }}
            />
          </LocalizationProvider>

          <PlanIntelligenceSuggestion
            itemType="goal"
            payload={{
              title,
              description: "",
              target_date: formatDateForStorage(targetDate),
            }}
            applyLabel="Use suggested goal"
            onApply={(suggestion) => {
              if (suggestion.suggested_title) setTitle(suggestion.suggested_title);
            }}
          />

          <TextField
            label="Where are you starting from? (optional)"
            value={startingValue}
            onChange={e => setStartingValue(e.target.value)}
            fullWidth
            inputProps={{ inputMode: "decimal", pattern: "[0-9., ]*" }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="This is your baseline so progress can be measured from today.">
                    <IconButton edge="end" size="small" aria-label="Starting point info">
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />

          {targetDate && (
            <Box>
              {(() => {
                const daysLeft = getDaysLeft(targetDate);
                let tone = "text.secondary";
                if (daysLeft !== null) {
                  if (daysLeft <= 7) tone = "error.main";
                  else if (daysLeft <= 30) tone = "warning.main";
                  else tone = "success.main";
                }
                return (
                  <Typography variant="body2" sx={{ color: tone }}>
                    {daysLeft !== null ? `${daysLeft} days left` : ""}
                  </Typography>
                );
              })()}
              <LinearProgress
                variant="determinate"
                value={0}
                sx={{ mt: 1 }}
              />
            </Box>
          )}

          {(title.trim() || targetDate) && (
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                p: 2,
                bgcolor: "background.paper",
              }}
            >
              <Typography variant="subtitle2">
                {title.trim() || "Your goal"}
              </Typography>
              {targetDate && (
                <Typography variant="caption" color="text.secondary">
                  {getDaysLeft(targetDate)} days left
                </Typography>
              )}
              <LinearProgress variant="determinate" value={0} sx={{ mt: 1 }} />
            </Box>
          )}

          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={
            loading ||
            !title.trim() ||
            !targetDate
          }
        >
          {loading ? "Creating..." : "Create goal"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default GoalCreateDialog;
